/**
 * Pool predictions page — debounced autosave per match, keyboard nav,
 * quick-fill chips, sticky day headers, mobile sticky CTA, lock countdowns.
 *
 * Server contract: POST /api/pools/:slug/predictions is upsert with atomic
 * SQL lock_time check (verified in server/routes-elysia/predictions.ts).
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Target, TrendingUp } from 'lucide-react';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/page-header';
import EmptyState from '../components/ui/empty-state';
import Skeleton from '../components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { TooltipProvider } from '../components/ui/tooltip';
import { usePool } from '../hooks/usePools';
import { useMatchesWithOptions } from '../hooks/useMatches';
import { useMyPredictions, useMyStats } from '../hooks/usePredictions';
import { formatMatchDate, getLocalDayKey, parseLocalDayKey } from '../lib/utils';
import type { Match, Prediction } from '../../shared/types';
import { classifyPoolPageErrorState } from './pool-page-error-state';
import { getPoolPredictionsMatchFilters } from './pool-predictions-filters';
import MatchPredictionCard from '../components/predictions/MatchPredictionCard';
import PredictionStickyBar from '../components/predictions/PredictionStickyBar';

export default function PoolPredictionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const poolSlug = slug ?? '';

  const { data: poolData, error: poolError } = usePool(poolSlug);
  const pool = poolData?.pool;
  const { data: matchesData, isLoading: matchesLoading, error: matchesError } =
    useMatchesWithOptions(getPoolPredictionsMatchFilters(pool), { enabled: Boolean(pool) });
  const { data: predictions, error: predictionsError } = useMyPredictions(poolSlug);
  const { data: stats } = useMyStats(poolSlug);

  const [pendingMatches, setPendingMatches] = useState<Set<number>>(new Set());

  const onPendingChange = useCallback((matchId: number, pending: boolean) => {
    setPendingMatches((prev) => {
      const next = new Set(prev);
      if (pending) next.add(matchId);
      else next.delete(matchId);
      return next;
    });
  }, []);

  const matches = useMemo(() => matchesData?.data ?? [], [matchesData?.data]);
  const predictionMap = useMemo(() => {
    const m = new Map<number, Prediction>();
    if (predictions) for (const p of predictions) m.set(p.matchId, p);
    return m;
  }, [predictions]);
  const matchesByDate = useMemo(() => {
    const acc: Record<string, Match[]> = {};
    for (const match of matches) {
      const day = getLocalDayKey(match.matchDate);
      if (!acc[day]) acc[day] = [];
      acc[day].push(match);
    }
    return acc;
  }, [matches]);

  const poolErrorState = classifyPoolPageErrorState({ hasPool: Boolean(pool), error: poolError });

  if (poolErrorState) {
    return (
      <EmptyState
        icon={<Target className="w-6 h-6" aria-hidden="true" />}
        title={poolErrorState.title}
        description={poolErrorState.description}
        action={
          <Link to="/boloes">
            <Button variant="outline">Voltar para bolões</Button>
          </Link>
        }
      />
    );
  }

  if (matchesError || predictionsError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Erro ao carregar palpites</AlertTitle>
        <AlertDescription>
          Não foi possível carregar jogos ou palpites desse bolão agora. Recarregue a página
          ou volte para o bolão.{' '}
          <Link to={`/boloes/${poolSlug}`} className="font-medium underline">
            Voltar para o bolão
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="max-w-3xl mx-auto pb-32 sm:pb-12">
        <PageHeader
          back={
            <Link
              to={`/boloes/${poolSlug}`}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para {pool?.name ?? 'o bolão'}
            </Link>
          }
          title="Meus palpites"
          description="Digite o placar previsto para cada jogo. Suas previsões são salvas automaticamente."
          actions={
            stats && (
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary-50 dark:bg-primary-500/10 px-3 py-1.5 text-xs font-semibold text-primary-700 dark:text-primary-300">
                <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" />
                {stats.totalPoints} pontos
              </span>
            )
          }
        />

        {matchesLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        ) : matches.length === 0 ? (
          <EmptyState
            icon={<Calendar className="w-6 h-6" aria-hidden="true" />}
            title="Nenhum jogo disponível"
            description="Quando o calendário oficial for divulgado, os jogos aparecerão aqui para você palpitar."
          />
        ) : (
          <div className="space-y-10">
            {Object.entries(matchesByDate).map(([day, dayMatches]) => (
              <section key={day}>
                <h2
                  className="sticky top-16 z-10 -mx-4 mb-4 bg-background/95 px-4 py-2 text-sm font-semibold text-muted-foreground backdrop-blur-md sm:top-20"
                >
                  {formatMatchDate(parseLocalDayKey(day))}
                </h2>
                <div className="space-y-3">
                  {dayMatches.map((match) => (
                    <MatchPredictionCard
                      key={match.id}
                      poolSlug={poolSlug}
                      match={match}
                      prediction={predictionMap.get(match.id)}
                      onPendingChange={onPendingChange}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      <PredictionStickyBar pendingCount={pendingMatches.size} />
    </TooltipProvider>
  );
}

